import React, { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../lib/api';
import { Icons } from '../components/icons';

type LicenseView = {
  mode?: string;
  path?: string;
  present?: boolean;
  enforce?: boolean;
  valid?: boolean;
  expired?: boolean;
  readonly?: boolean;
  customer?: string;
  edition?: string;
  installed_edition?: string;
  issued_at?: string;
  expires_at?: string;
  modules?: string[];
  quotas?: { max_users?: number; max_stations?: number; max_storage_gb?: number };
  notes?: string;
  reason?: string;
};

const emptyDoc = () => ({
  edition: 'factory',
  customer: '',
  issued_at: new Date().toISOString().slice(0, 10) + 'T00:00:00Z',
  expires_at: new Date(Date.now() + 365 * 86400000).toISOString().slice(0, 10) + 'T00:00:00Z',
  machine_id: '',
  modules: ['sop', 'mes', 'les', 'qms', 'eam'],
  quotas: { max_users: 100, max_stations: 50, max_storage_gb: 50 },
  notes: '',
  enforce: true,
  sig: '',
});

export function LicenseSettings() {
  const [info, setInfo] = useState<LicenseView | null>(null);
  const [jsonText, setJsonText] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    setMsg('');
    apiFetch('/api/v1/platform/license').then((d) => {
      if (d.code === 0 && d.data) {
        setInfo(d.data as LicenseView);
        const data = d.data as LicenseView;
        setJsonText(JSON.stringify({
          edition: data.edition || 'factory',
          customer: data.customer || '',
          issued_at: data.issued_at || '',
          expires_at: data.expires_at || '',
          machine_id: '',
          modules: data.modules || [],
          quotas: data.quotas || {},
          notes: data.notes || '',
          enforce: data.enforce !== false,
          sig: '',
        }, null, 2));
      } else {
        setMsg(d.message || '加载失败');
        setJsonText(JSON.stringify(emptyDoc(), null, 2));
      }
      setLoading(false);
    }).catch(() => {
      setMsg('网络错误');
      setLoading(false);
    });
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    setMsg('');
    let body: Record<string, unknown>;
    try {
      body = JSON.parse(jsonText);
    } catch {
      setMsg('JSON 无法解析');
      setSaving(false);
      return;
    }
    const d = await apiFetch('/api/v1/platform/license', { method: 'PUT', body });
    if (d.code === 0) {
      setMsg('已写入授权文件');
      setInfo(d.data as LicenseView);
    } else {
      setMsg(d.message || '保存失败');
    }
    setSaving(false);
  };

  const statusColor = info?.readonly ? '#dc2626' : info?.enforce ? '#16a34a' : '#2563eb';

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ fontSize: 18, fontWeight: 650 }}>软件授权</h2>
        <button className="btn btn-outline btn-xs" onClick={load} disabled={loading}>
          <Icons.Refresh w="12" h="12" /> 刷新
        </button>
      </div>
      {msg ? <div className="card" style={{ padding: 12, marginBottom: 16, fontSize: 13 }}>{msg}</div> : null}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-title">当前状态</div>
        {loading ? <p style={{ fontSize: 13, color: 'var(--brand-text-muted)' }}>加载中…</p> : (
          <div style={{ fontSize: 13, lineHeight: 1.8 }}>
            <div>模式 <strong>{info?.mode || 'auto'}</strong>
              {info?.installed_edition ? ` · 安装包 ${info.installed_edition}` : ''}
              {info?.path ? ` · ${info.path}` : ''}</div>
            <div>状态 <strong style={{ color: statusColor }}>
              {info?.readonly ? '只读 / 无效' : info?.enforce ? '已强制' : (info?.present ? '文件在、未强制' : '无文件（不强制）')}
            </strong></div>
            {info?.customer ? <div>客户 {info.customer}{info.edition ? ` · ${info.edition}` : ''}</div> : null}
            {info?.expires_at ? <div>到期 {String(info.expires_at).slice(0, 10)}</div> : null}
            {info?.reason ? <div style={{ color: 'var(--brand-text-muted)' }}>{info.reason}</div> : null}
            {info?.modules && info.modules.length > 0 ? <div>模块 {info.modules.join('、')}</div> : null}
            {info?.quotas ? <div>配额 用户 {info.quotas.max_users || '不限'} · 工位 {info.quotas.max_stations || '不限'} · 存储 {info.quotas.max_storage_gb || '不限'} GB</div> : null}
          </div>
        )}
      </div>
      <div className="card">
        <div className="card-title">导入 / 更新 license.json</div>
        <p style={{ fontSize: 12, color: 'var(--brand-text-muted)', marginBottom: 8, lineHeight: 1.6 }}>
          客户交付请设 <code>enforce: true</code>。测试服和联调机保持 <code>enforce: false</code> 或环境变量 <code>GRIDORBIT_LICENSE_MODE=dev</code>。
          写入失败时用 root 覆盖 <code>/etc/gridorbit/license.json</code> 后重启网关。
        </p>
        <textarea
          className="form-input"
          style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12, minHeight: 280, width: '100%' }}
          value={jsonText}
          onChange={(e) => setJsonText(e.target.value)}
        />
        <div style={{ marginTop: 12 }}>
          <button className="btn btn-primary btn-xs" disabled={saving} onClick={save}>
            {saving ? '写入中…' : '写入授权文件'}
          </button>
        </div>
      </div>
    </div>
  );
}
